/**
 * Admin Agent: Manages the allowed-emails allowlist.
 * Provides hooks for listing, adding, and removing emails.
 */

import { useState, useEffect, useCallback } from 'react';
import APIAgent from './apiAgent';

const ENDPOINT = '/api/admin/allowed-emails';

/**
 * Check whether the current user has admin access to the allowlist endpoint.
 * @returns {Promise<boolean>}
 */
export async function checkAdminAccess() {
  try {
    await APIAgent.request(ENDPOINT, 'GET');
    return true;
  } catch {
    return false;
  }
}

/**
 * Hook that manages the allowed-emails list.
 *
 * @returns {{
 *   entries: Array,
 *   unregisteredUsers: Array,
 *   loading: boolean,
 *   error: string|null,
 *   isAdmin: boolean|null,
 *   addEmail: (email: string) => Promise<void>,
 *   removeEmail: (email: string) => Promise<void>,
 *   refresh: () => Promise<void>,
 * }}
 */
export function useAllowedEmails() {
  const [entries, setEntries] = useState([]);
  const [unregisteredUsers, setUnregisteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null); // null = unknown

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(ENDPOINT, 'GET');
      setEntries(data.entries || []);
      setUnregisteredUsers(data.unregistered_users || []);
      setIsAdmin(true);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('403') || msg.toLowerCase().includes('admin')) {
        setIsAdmin(false);
      } else {
        setError(msg || 'Failed to load access control list');
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addEmail = useCallback(
    async (email) => {
      await APIAgent.request(ENDPOINT, 'POST', { email });
      await refresh();
    },
    [refresh]
  );

  const removeEmail = useCallback(
    async (email) => {
      await APIAgent.request(`${ENDPOINT}/${encodeURIComponent(email)}`, 'DELETE');
      await refresh();
    },
    [refresh]
  );

  return { entries, unregisteredUsers, loading, error, isAdmin, addEmail, removeEmail, refresh };
}
